import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { Calendar as CalendarIcon, Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverAnchor,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"

interface DatePickerProps {
  value?: string
  onChange: (date: string) => void
  label?: string
  className?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
}

export function DatePicker({
  value,
  onChange,
  className,
  disabled = false,
  minDate,
  maxDate,
}: DatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(
    value ? new Date(`${value}T00:00:00`) : undefined
  )
  const [inputValue, setInputValue] = React.useState<string>("")
  const [month, setMonth] = React.useState<Date>(new Date())
  const [isOpen, setIsOpen] = React.useState(false)
  const [error, setError] = React.useState<string | undefined>()

  // Calendar year range: 1900 to current year + 10
  const fromYear = 1900
  const toYear = new Date().getFullYear() + 10

  React.useEffect(() => {
    if (value) {
      const parsed = new Date(`${value}T00:00:00`)
      if (isValid(parsed)) {
        setDate(parsed)
        setMonth(parsed)
        setInputValue(format(parsed, "MM/dd/yyyy"))
        setError(undefined)
      } else {
        setDate(undefined)
        setInputValue("")
      }
    } else {
      setDate(undefined)
      setInputValue("")
    }
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    
    // Explicitly open popup when user starts typing if not already open
    if (!isOpen && val.length > 0) {
      setIsOpen(true)
    }

    if (val.length === 10) {
      let parsedDate = parse(val, "MM/dd/yyyy", new Date())
      if (!isValid(parsedDate)) {
        parsedDate = parse(val, "MM-dd-yyyy", new Date())
      }

      if (isValid(parsedDate)) {
        setDate(parsedDate)
        setMonth(parsedDate)
        onChange(format(parsedDate, "yyyy-MM-dd"))
        setError(undefined)
      } else {
        setError("Invalid date (MM/DD/YYYY)")
      }
    } else if (val.length > 0 && val.length < 10) {
      setError(undefined)
    } else if (val.length === 0) {
      setError(undefined)
      setDate(undefined)
      onChange("")
    }
  }

  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate)
    if (selectedDate) {
      setMonth(selectedDate)
      setInputValue(format(selectedDate, "MM/dd/yyyy"))
      onChange(format(selectedDate, "yyyy-MM-dd"))
      setError(undefined)
    }
    // Keep popover open as requested
  }

  const handleDone = () => {
    setIsOpen(false)
    setError(undefined)
  }

  const handleClose = () => {
    setIsOpen(false)
    setError(undefined)
  }

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isOpen) {
      // Small timeout to ensure the transition is smooth or the DOM is ready
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  return (
    <div className="flex flex-col w-full gap-1 group/datepicker">
      <Popover open={isOpen} onOpenChange={(open) => {
        // Only allow closing via handleClose/handleDone
        // But Radix needs this for internal consistency
        setIsOpen(open)
      }}>
        <PopoverAnchor asChild>
          <div className="relative flex items-center w-full">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => setIsOpen(true)}
              onClick={() => !isOpen && setIsOpen(true)}
              placeholder="MM/DD/YYYY"
              disabled={disabled}
              className={cn(
                "h-8 pr-12 shadow-sm transition-all duration-200 text-xs",
                "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800",
                "focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5",
                error && "border-red-500 focus:border-red-600 focus:ring-red-500/5",
                className
              )}
            />
            <div className="absolute right-0 top-0 h-full flex items-center pr-1.5 gap-0.5">
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  className="h-6 w-6 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-all"
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
            </div>
          </div>
        </PopoverAnchor>
        
        <PopoverContent 
          className="p-0 border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 w-[260px] rounded-xl" 
          align="start" 
          sideOffset={4}
          onPointerDownOutside={(e) => e.preventDefault()}
          // Re-focus the input if user interacts with the popover but focus escapes
          onFocusOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col bg-white dark:bg-slate-950">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleSelect}
              month={month}
              onMonthChange={setMonth}
              captionLayout="dropdown"
              fromYear={fromYear}
              toYear={toYear}
              disabled={(date) => {
                if (disabled) return true
                if (minDate && date < minDate) {
                  // Allow the same day if we just check dates
                  const d1 = new Date(date).setHours(0, 0, 0, 0)
                  const d2 = new Date(minDate).setHours(0, 0, 0, 0)
                  if (d1 < d2) return true
                }
                if (maxDate && date > maxDate) {
                  const d1 = new Date(date).setHours(0, 0, 0, 0)
                  const d2 = new Date(maxDate).setHours(0, 0, 0, 0)
                  if (d1 > d2) return true
                }
                return false
              }}
              className="p-1.5"
              formatters={{
                formatWeekdayName: (date) => format(date, "EEEEEE"), // 2-letter day names (Su, Mo, etc.)
              }}
              classNames={{
                root: "w-full",
                months: "flex flex-col",
                month: "space-y-1",
                caption: "flex items-center justify-center px-1 mb-1 relative h-8", 
                caption_label: "hidden", 
                nav: "hidden", 
                table: "w-full border-collapse",
                head_row: "flex justify-between mb-1",
                head_cell: "text-slate-400 w-full font-bold text-[0.6rem] uppercase text-center p-[2px] opacity-80", 
                row: "flex w-full justify-between mt-0.5",
                cell: "flex-1 text-center text-[10px] p-0 relative h-8 flex items-center justify-center",
                day: cn(
                  "h-7 w-7 p-0 font-bold hover:bg-slate-100 rounded-lg transition-all flex items-center justify-center text-[11px]"
                ),
                day_selected: "bg-blue-600 text-white hover:bg-blue-700 shadow-sm scale-110",
                day_today: "border border-blue-100 text-blue-600",
                day_outside: "text-slate-300 opacity-50",
                day_disabled: "text-slate-100 opacity-50",
                dropdowns: "flex gap-1 items-center justify-center grow z-10 h-8",
                dropdown: "text-[11px] font-black bg-transparent border-none appearance-none cursor-pointer hover:text-blue-600 transition-colors py-0 focus:outline-none",
                dropdown_root: "relative flex items-center bg-slate-50 dark:bg-slate-900 rounded-lg px-2 py-0 h-6.5 border border-slate-100 dark:border-slate-800 shadow-sm",
              }}
            />
            
            <div className="flex items-center justify-between p-2 px-3 border-t bg-slate-50/50 dark:bg-slate-900/50 gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClose}
                className="h-7 text-[10px] font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2"
              >
                Close
              </Button>
              <Button 
                size="sm" 
                onClick={handleDone}
                className="h-7 px-3 text-[10px] font-black bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-500/20 text-white flex items-center gap-1 rounded-lg"
              >
                <Check className="h-3 w-3" />
                DONE
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && (
        <div className="flex items-center gap-1 ml-1 animate-in slide-in-from-top-0.5 duration-200">
          <Check className="h-2.5 w-2.5 text-red-500 rotate-45" />
          <p className="text-[9px] text-red-500 font-black tracking-tighter uppercase italic">
            {error}
          </p>
        </div>
      )}
    </div>
  )
}
